import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  Alert,
  Divider,
  Link as MuiLink,
} from '@mui/material';
import { acceptChefTerms } from './chefApi';

const sectionHeaderSx = {
  fontWeight: 700,
  mt: 3,
  mb: 1,
  color: 'text.primary',
};

const clauseBoxSx = {
  p: 2,
  mb: 2,
  bgcolor: 'grey.50',
  borderLeft: '3px solid',
  borderColor: 'primary.main',
};

const ChefAgreement: React.FC = () => {
  const [agreed, setAgreed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await acceptChefTerms();
      setAccepted(true);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to accept agreement. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (accepted) {
    return (
      <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: 600, mx: 'auto', mt: 8, textAlign: 'center' }}>
        <Typography variant="h4" fontWeight="bold" gutterBottom>
          Welcome to the Chef Collective!
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mt: 2, mb: 3, lineHeight: 1.8 }}>
          Thank you for joining the Snack'd Chef Collective. We're excited to bring your recipes to
          families everywhere!
        </Typography>
        <Paper sx={{ p: 3, mb: 3, bgcolor: 'grey.50', textAlign: 'left' }}>
          <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
            Head to your <strong>Chef Dashboard</strong> to view your recipes, promo code,
            payment details, and more.
          </Typography>
        </Paper>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          If you have any questions, please contact us at{' '}
          <MuiLink href="mailto:support@snackdapp.com">support@snackdapp.com</MuiLink>
        </Typography>
        <Button
          variant="contained"
          size="large"
          component={RouterLink}
          to="/chef-dashboard"
        >
          Go to Chef Dashboard
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: 800, mx: 'auto', mt: 4 }}>
      <Typography variant="h4" fontWeight="bold" gutterBottom>
        Chef Collective Agreement
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Please review and accept the following agreement to access your chef dashboard.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper
        sx={{
          p: { xs: 2, sm: 3 },
          mb: 3,
          maxHeight: 500,
          overflow: 'auto',
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Typography variant="h5" fontWeight="bold" gutterBottom>
          Snack'd Chef Collective Agreement
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          Effective Date: February 2026 | Version: 1.0
        </Typography>

        <Divider sx={{ mb: 2 }} />

        <Typography variant="body2" paragraph>
          This Chef Collective Agreement ("Agreement") is entered into between Snackd LLC,
          a Connecticut limited liability company ("Company," "we," "us"), and you, the culinary
          professional agreeing to these terms ("Chef," "you"). By accepting this Agreement,
          you agree to participate in the Snackd Chef Collective under the following terms.
        </Typography>

        {/* Section 1 */}
        <Typography variant="subtitle1" sx={sectionHeaderSx}>
          1. Program Overview
        </Typography>
        <Paper variant="outlined" sx={clauseBoxSx}>
          <Typography variant="body2" paragraph>
            The Snackd Chef Collective is a curated program that brings recipes from real culinary
            professionals into the Snackd meal planning platform. Chefs contribute original recipes
            that Snackd users can discover, cook, and enjoy as part of their personalized meal plans.
          </Typography>
          <Typography variant="body2" paragraph sx={{ mb: 0 }}>
            This is a <strong>non-exclusive</strong> arrangement. You retain full ownership of your
            recipes and are free to publish, sell, or license them elsewhere. Snackd may work with
            other chefs at our discretion.
          </Typography>
        </Paper>

        {/* Section 2 */}
        <Typography variant="subtitle1" sx={sectionHeaderSx}>
          2. Recipe Submissions
        </Typography>
        <Paper variant="outlined" sx={clauseBoxSx}>
          <Typography variant="body2" paragraph>
            You agree to submit between 10 and 20 original recipes suitable for inclusion in the
            Snackd platform. Each submitted recipe must include:
          </Typography>
          <Typography variant="body2" component="div">
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              <li style={{ marginBottom: 8 }}>
                Recipe title, complete ingredient list with quantities, step-by-step preparation
                instructions, prep time and cook time, serving size, and any relevant dietary or
                allergen information.
              </li>
              <li style={{ marginBottom: 8 }}>
                <strong>Suitability:</strong> Recipes must be designed for home cooks with standard
                kitchen equipment. Restaurant-level complexity, specialty equipment, or hard-to-source
                ingredients should be avoided. Recipes should be suitable for families, including
                households with children.
              </li>
              <li>
                <strong>Submission process:</strong> Recipes will be submitted in a format specified
                by Snackd during onboarding. Snackd reserves the right to request reasonable revisions
                for clarity, formatting consistency, or platform compatibility before acceptance.
              </li>
            </ul>
          </Typography>
        </Paper>

        {/* Section 3 */}
        <Typography variant="subtitle1" sx={sectionHeaderSx}>
          3. Originality & Intellectual Property
        </Typography>
        <Paper variant="outlined" sx={clauseBoxSx}>
          <Typography variant="body2" component="div">
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              <li style={{ marginBottom: 8 }}>
                <strong>Originality warranty:</strong> You represent and warrant that all recipes you
                submit are your original creations, or that you have the legal right to submit them.
                You further warrant that the recipes do not infringe upon the intellectual property,
                copyright, or other rights of any third party.
              </li>
              <li style={{ marginBottom: 8 }}>
                <strong>Ownership:</strong> You retain full ownership of your recipes. Nothing in this
                Agreement transfers ownership of your recipes to Snackd. You are free to publish, sell,
                license, or use your recipes in any other context.
              </li>
              <li style={{ marginBottom: 8 }}>
                <strong>License grant:</strong> By submitting recipes under this Agreement, you grant
                Snackd a <strong>non-exclusive, worldwide license</strong> to display, distribute,
                reproduce, and adapt your recipes within the Snackd platform, associated marketing
                materials, and social media channels for the duration of the Agreement term. This
                includes the right to adjust recipes for dietary modifications and to include recipes
                in AI-generated meal plans.
              </li>
              <li>
                <strong>Attribution:</strong> Snackd will credit you by name on every recipe displayed
                to users (e.g., "By Chef [Your Name]"). Your attribution will not be removed or
                obscured during the term of this Agreement.
              </li>
            </ul>
          </Typography>
        </Paper>

        {/* Section 4 */}
        <Typography variant="subtitle1" sx={sectionHeaderSx}>
          4. Compensation
        </Typography>
        <Paper variant="outlined" sx={clauseBoxSx}>
          <Typography variant="body2" component="div">
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              <li style={{ marginBottom: 8 }}>
                <strong>Recipe contribution fee:</strong> Snackd will pay you <strong>$30.00 per
                accepted recipe, per year</strong>. Payment is calculated based on the number of
                recipes accepted into the platform at the start of each Agreement term.
              </li>
              <li style={{ marginBottom: 8 }}>
                <strong>Payment schedule:</strong> The full annual contribution fee will be paid
                within <strong>30 days</strong> of your recipes being accepted and published on the
                platform. For renewals, payment will be issued within 30 days of the renewal date.
              </li>
              <li style={{ marginBottom: 8 }}>
                <strong>Payment method:</strong> All payments are made via <strong>PayPal</strong>.
                You are responsible for maintaining a valid PayPal account and providing accurate
                payment details.
              </li>
              <li>
                <strong>Additional recipes:</strong> You may submit additional recipes during the
                Agreement term. Accepted additions will be compensated at the same per-recipe rate,
                prorated for the remaining months in the term.
              </li>
            </ul>
          </Typography>
        </Paper>

        {/* Section 5 */}
        <Typography variant="subtitle1" sx={sectionHeaderSx}>
          5. Chef Profile & Promotional Rights
        </Typography>
        <Paper variant="outlined" sx={clauseBoxSx}>
          <Typography variant="body2" paragraph>
            As part of the Chef Collective, you agree to provide: a professional bio (100-300 words),
            a headshot or professional photo, and links to your website, social media, or other
            relevant online presence.
          </Typography>
          <Typography variant="body2" component="div">
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              <li style={{ marginBottom: 8 }}>
                <strong>Likeness and promotional use:</strong> You grant Snackd permission to use your
                name, photo, bio, and submitted profile materials to promote your recipes and the Chef
                Collective program. This includes use on the Snackd website, app, social media channels,
                email newsletters, and press materials.
              </li>
              <li style={{ marginBottom: 8 }}>
                <strong>Spotlight features:</strong> Snackd may feature you in Chef Spotlight weeks,
                cultural celebration themes, blog posts, or other promotional campaigns. Snackd will
                make reasonable efforts to notify you in advance of major features, but advance approval
                is not required for standard platform promotion.
              </li>
              <li>
                <strong>Promotional tools:</strong> You will receive a personal discount code to share
                with your followers, providing them a 30-day free trial of Snackd. Snackd may also
                provide co-branded social media assets for your optional use.
              </li>
            </ul>
          </Typography>
        </Paper>

        {/* Section 6 */}
        <Typography variant="subtitle1" sx={sectionHeaderSx}>
          6. Content Standards & Conduct
        </Typography>
        <Paper variant="outlined" sx={clauseBoxSx}>
          <Typography variant="body2" paragraph>
            As a member of the Chef Collective, you represent the Snackd brand to our user community.
          </Typography>

          <Box sx={{ p: 2, mb: 2, bgcolor: '#f0fdf4', border: '1px solid', borderColor: '#86efac', borderRadius: 1 }}>
            <Typography variant="subtitle2" fontWeight="bold" gutterBottom color="success.dark">
              You may:
            </Typography>
            <Typography variant="body2" component="div" color="success.dark">
              <ul style={{ margin: 0, paddingLeft: 20 }}>
                <li>Promote your participation in the Chef Collective on your own channels</li>
                <li>Share your Snackd recipes and profile with your audience</li>
                <li>Use any co-branded assets Snackd provides</li>
                <li>Describe the program accurately</li>
              </ul>
            </Typography>
          </Box>

          <Box sx={{ p: 2, mb: 0, bgcolor: '#fef9c3', border: '1px solid', borderColor: '#fde047', borderRadius: 1 }}>
            <Typography variant="subtitle2" fontWeight="bold" gutterBottom color="warning.dark">
              You may not:
            </Typography>
            <Typography variant="body2" component="div" color="warning.dark">
              <ul style={{ margin: 0, paddingLeft: 20 }}>
                <li>Make false or misleading claims about Snackd</li>
                <li>Claim to be an employee or co-owner of Snackd</li>
                <li>Submit recipes that are copied or substantially derived from another chef's published work</li>
                <li>Create content associated with Snackd that is hateful, discriminatory, or inappropriate for a family audience</li>
                <li>Use your Snackd affiliation to promote competing meal planning services</li>
              </ul>
            </Typography>
          </Box>
        </Paper>

        {/* Section 7 */}
        <Typography variant="subtitle1" sx={sectionHeaderSx}>
          7. Term & Renewal
        </Typography>
        <Paper variant="outlined" sx={clauseBoxSx}>
          <Typography variant="body2" component="div">
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              <li style={{ marginBottom: 8 }}>
                <strong>Initial term:</strong> This Agreement is effective for <strong>one (1) year</strong> from
                the date of execution.
              </li>
              <li style={{ marginBottom: 8 }}>
                <strong>Renewal:</strong> The Agreement will automatically renew for successive one-year
                terms unless either party provides written notice of non-renewal at least <strong>30
                days</strong> before the end of the current term. Upon renewal, the annual recipe
                contribution fee will be recalculated based on the number of active recipes on the platform.
              </li>
              <li>
                <strong>Adding recipes at renewal:</strong> At each renewal, you may submit additional
                recipes for consideration. Snackd may also invite you to contribute new recipes for
                upcoming features or themes.
              </li>
            </ul>
          </Typography>
        </Paper>

        {/* Section 8 */}
        <Typography variant="subtitle1" sx={sectionHeaderSx}>
          8. Termination
        </Typography>
        <Paper variant="outlined" sx={clauseBoxSx}>
          <Typography variant="body2" paragraph>
            Either party may terminate this Agreement at any time by providing <strong>30 days'
            written notice</strong> (email is sufficient).
          </Typography>
          <Typography variant="body2" component="div">
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              <li style={{ marginBottom: 8 }}>
                <strong>Upon termination:</strong> Snackd will remove your recipes from the platform
                within 30 days of the termination effective date. Your chef profile will be deactivated.
                Any personal discount codes issued to you will be deactivated. Compensation already paid
                for the current term is non-refundable.
              </li>
              <li style={{ marginBottom: 8 }}>
                <strong>Termination for cause:</strong> Snackd may terminate this Agreement immediately
                and without notice if you materially breach these terms, submit plagiarized recipes, or
                engage in conduct that harms the Snackd brand or community. In cases of termination for
                cause, Snackd may reclaim prorated compensation for the remaining term.
              </li>
              <li>
                <strong>Post-termination:</strong> Upon termination, the license granted in Section 3
                ends and Snackd will cease displaying your recipes. You must cease using any Snackd brand
                materials within 7 days. Sections covering originality warranties, limitation of liability,
                and indemnification survive termination.
              </li>
            </ul>
          </Typography>
        </Paper>

        {/* Section 9 */}
        <Typography variant="subtitle1" sx={sectionHeaderSx}>
          9. Tax Responsibility
        </Typography>
        <Paper variant="outlined" sx={clauseBoxSx}>
          <Typography variant="body2" paragraph sx={{ mb: 0 }}>
            You are an independent contractor, not an employee of Snackd LLC. You are solely responsible
            for reporting and paying all applicable taxes on compensation earned through this program.
            Snackd may request a completed W-9 form (or equivalent) and may issue a 1099-NEC for annual
            earnings of $600 or more, as required by law.
          </Typography>
        </Paper>

        {/* Section 10 */}
        <Typography variant="subtitle1" sx={sectionHeaderSx}>
          10. Confidentiality
        </Typography>
        <Paper variant="outlined" sx={clauseBoxSx}>
          <Typography variant="body2" paragraph sx={{ mb: 0 }}>
            Any non-public information shared with you as part of this program — including compensation
            details, platform roadmap, upcoming features, and other chefs' participation terms — is
            confidential. You agree not to disclose these details publicly or to third parties without
            prior written consent from Snackd.
          </Typography>
        </Paper>

        {/* Section 11 */}
        <Typography variant="subtitle1" sx={sectionHeaderSx}>
          11. Limitation of Liability & Indemnification
        </Typography>
        <Paper variant="outlined" sx={clauseBoxSx}>
          <Typography variant="body2" paragraph>
            Snackd LLC provides this program "as is." To the maximum extent permitted by law, Snackd
            shall not be liable for any indirect, incidental, or consequential damages arising from
            your participation in this program. Our total liability to you shall not exceed the total
            compensation paid to you in the 12 months preceding any claim.
          </Typography>
          <Typography variant="body2" paragraph sx={{ mb: 0 }}>
            You agree to indemnify and hold harmless Snackd LLC from any claims, damages, or expenses
            arising from your submitted recipes (including but not limited to intellectual property
            infringement claims), your promotional activities, or your violation of this Agreement.
          </Typography>
        </Paper>

        {/* Section 12 */}
        <Typography variant="subtitle1" sx={sectionHeaderSx}>
          12. Future Programs
        </Typography>
        <Paper variant="outlined" sx={clauseBoxSx}>
          <Typography variant="body2" paragraph sx={{ mb: 0 }}>
            Snackd may introduce additional programs, tiers, or partnership opportunities for Chef
            Collective members in the future (e.g., a Founding Chef program with enhanced compensation
            or other benefits). Any such programs will be governed by separate agreements. Nothing in
            this Agreement guarantees participation in or eligibility for future programs.
          </Typography>
        </Paper>

        {/* Section 13 */}
        <Typography variant="subtitle1" sx={sectionHeaderSx}>
          13. Modifications
        </Typography>
        <Paper variant="outlined" sx={clauseBoxSx}>
          <Typography variant="body2" paragraph sx={{ mb: 0 }}>
            Snackd reserves the right to modify this Agreement at any time. We will notify you of
            material changes via email at least <strong>30 days</strong> before they take effect.
            Your continued participation in the program after such changes constitutes acceptance
            of the updated terms.
          </Typography>
        </Paper>

        {/* Section 14 */}
        <Typography variant="subtitle1" sx={sectionHeaderSx}>
          14. Governing Law
        </Typography>
        <Paper variant="outlined" sx={clauseBoxSx}>
          <Typography variant="body2" paragraph sx={{ mb: 0 }}>
            This Agreement shall be governed by and construed in accordance with the laws of the
            State of Connecticut, without regard to its conflict of law provisions.
          </Typography>
        </Paper>

        <Divider sx={{ my: 3 }} />

        <Typography variant="body2" color="text.secondary">
          By checking the box below and clicking "Accept & Continue," you acknowledge that you have
          read, understood, and agree to be bound by the terms of this Chef Collective Agreement.
        </Typography>
      </Paper>

      <FormControlLabel
        control={
          <Checkbox
            checked={agreed}
            onChange={(e) => setAgreed(e.target.checked)}
          />
        }
        label="I have read and agree to the Chef Collective Agreement"
        sx={{ mb: 3 }}
      />

      <Box>
        <Button
          variant="contained"
          size="large"
          onClick={handleSubmit}
          disabled={!agreed || submitting}
        >
          {submitting ? <CircularProgress size={24} /> : 'Accept & Continue'}
        </Button>
      </Box>
    </Box>
  );
};

export default ChefAgreement;
